import { computed, inject, Injectable, Signal } from '@angular/core';
import { ActiveCompanyService } from './active-company.service';
import { AuthService } from './auth.service';
import { LocalStorageService } from './local-storage.service';

/**
 * SessionScopeService - Stable identity of "who is working in which company" for scoping cached data and
 * late HTTP results.
 *
 * {@link key} is `undefined` when there is no signed-in user, no tokens (logout or failed refresh) or no active
 * company. It only changes when the signed-in user or the active company changes: an access-token refresh
 * (a new `tokens()` object after a 401 retry) keeps the same key, so in-flight results are still delivered.
 *
 * Scope: Global (`providedIn: 'root'`).
 */
@Injectable({ providedIn: 'root' })
export class SessionScopeService {
  private readonly currentUser = inject(AuthService).currentUser;
  private readonly tokens = inject(LocalStorageService).tokens;
  private readonly activeCompanyId = inject(ActiveCompanyService).activeCompanyId;

  public readonly key: Signal<string | undefined> = computed(() => {
    const userId = this.currentUser()?.id;
    const companyId = this.activeCompanyId();
    return userId && companyId && this.tokens() ? JSON.stringify([userId, companyId]) : undefined;
  });
}
